import {
  queryOptions,
  type QueryClient,
  type QueryKey,
  type UseQueryResult,
} from "@tanstack/react-query"

import type { RouterAuthState } from "./router-context"

import { authApi, type AuthUser } from "./auth-api"

export const AUTH_ME_QUERY_KEY = ["auth", "me"] as const

const AUTH_QUERY_MAX_AGE_MS = 10_000

export const authMeQueryOptions = () => {
  return queryOptions({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: authApi.me,
    retry: false,
  })
}

export const toRouterAuthState = (
  meQuery: UseQueryResult<AuthUser | null, Error>,
): RouterAuthState => {
  if (meQuery.data) {
    return {
      user: meQuery.data,
      status: "authenticated",
    }
  }

  if (meQuery.isPending) {
    return {
      user: null,
      status: "pending",
    }
  }

  if (meQuery.isError) {
    return {
      user: null,
      status: "pending",
    }
  }

  return {
    user: null,
    status: "guest",
  }
}

export const resolveAuth = async (
  queryClient: QueryClient,
): Promise<AuthUser | null> => {
  return queryClient.ensureQueryData({
    ...authMeQueryOptions(),
    revalidateIfStale: true,
  })
}

export const isQueryFresh = (
  queryClient: QueryClient,
  queryKey: QueryKey,
  maxAgeMs = AUTH_QUERY_MAX_AGE_MS,
): boolean => {
  const state = queryClient.getQueryState(queryKey)

  if (!state?.dataUpdatedAt) {
    return false
  }

  return Date.now() - state.dataUpdatedAt <= maxAgeMs
}

export const isAuthQueryFresh = (queryClient: QueryClient): boolean => {
  return isQueryFresh(queryClient, AUTH_ME_QUERY_KEY)
}

export const resolveFreshAuth = async (
  queryClient: QueryClient,
): Promise<AuthUser | null> => {
  if (isAuthQueryFresh(queryClient)) {
    return queryClient.getQueryData<AuthUser | null>(AUTH_ME_QUERY_KEY) ?? null
  }

  return queryClient.fetchQuery(authMeQueryOptions())
}
